import * as _ from 'lodash';
import * as hash from 'object-hash';
import * as moment from 'moment';
import { useContext, useEffect, useReducer, useState } from 'react';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, AppActionCreators, RecipientActionCreators, FileActionCreators, IAppState, IRecipientGeneralDataResponse, IRecipientProfileRecordsPostResponse } from '@nkr/store/index';
import { IRecipientGeneral, IRecipientGeneralDataRequest, IFileUpload } from '@nkr/models/index';
import { FileDocumentType } from '@nkr/enums/document-type';
import { downloadFile as downloadFileUtil } from '@nkr/services/download-file';
import { AfterAction, RecipientProfileContext, ToggleType } from '../models';
import { generalReducer, initialState } from './state';
import { ProfileWrapper } from '../styles';
import { toast } from 'react-toastify';
import Content from './general';
import { ILookupData } from '../lookup-data';
import DuplicateRecordModal from '@nkr/components/duplicate-record-modal';

interface IProps {
    saveRef: React.MutableRefObject<any>,
    lookupData: ILookupData
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getGeneral: (centerId: number, recipientId: number) => Promise<IRecipientGeneralDataResponse>,
    updateGeneral: (centerId: number, recipientId: number, data: IRecipientGeneralDataRequest) => Promise<IRecipientGeneralDataResponse>,
    downloadFile: (docType: number, id: number) => Promise<any>,
    uploadFile: (centerId: number, recipientId: number, docType: number, file: IFileUpload) => Promise<IRecipientProfileRecordsPostResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

const General = (props: TProps) => {
    const context = useContext(RecipientProfileContext);
    const [state, dispatch] = useReducer(generalReducer, initialState);
    const [newConsentFile, setNewConsentFile] = useState<File>(null);
    const [showDuplicateRecord, setShowDuplicateRecord] = useState(false);
    const [duplicateMessage, setDuplicateMessage] = useState<string>('');

    useEffect(() => {
        getData();
    }, []);

    // apiPut resolves with the axios error instead of rejecting, so a 409 arrives in .then
    // rather than .catch. Reads both shapes. BM-9682.
    const handleDuplicateError = (error: any): boolean => {
        if (error?.response?.status != 409) {
            return false;
        }

        setDuplicateMessage(error.response.data);
        setShowDuplicateRecord(true);

        return true;
    };

    const handleCloseDuplicateRecord = () => {
        setShowDuplicateRecord(false);
    };

    const getData = () => {
        props.showLoading('Getting general information, one moment please....').then(() => {
            props.getGeneral(context.centerId, context.recipientId)
                .then(response => {
                    if (response.data.success) {
                        let data = response.data.results;

                        isInactiveReasonOutdated(data);

                        dispatch({
                            type: 'afterLoad',
                            value: {
                                data,
                                hash: hash(data),
                            }
                        });

                    }
                }).catch(() => {

                }).finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleDataChange = (data: IRecipientGeneral) => {
        let newHash = hash(data);

        var hasChanged = state.hash != newHash;

        dispatch({
            type: 'updateData',
            value: data
        });

        context.updateHasChanges(hasChanged);
    };

    const isInactiveReasonOutdated = ( data: IRecipientGeneral ) : boolean => {
        let result                  = false,
            inactiveReasonMoment    = moment(data?.inactiveReasonDate)
        ;

        if( data?.inactiveReasonDate?.length > 0 )
        {
            result                  = inactiveReasonMoment.isValid() && inactiveReasonMoment.isBefore(moment().subtract(90, 'days'));

            context.updateAllowSaveWithNoChanges(result);
        }

        return result;
    };

    const handleViewFile = () => {
        props.downloadFile(FileDocumentType.VoucherForm, context.recipientId)
            .then((response: any) => {
                downloadFileUtil(response.data, '', response.data.type);
            })
            .catch(() => {
                toast.error("Unable to download file at this time.  Please contact support.");
            });
    };

    const handleChangeConsentFile = (file: File) => {
        setNewConsentFile(file);
        context.updateHasChanges(true);
    };

    const handleRemoveConsentFile = () => {
        setNewConsentFile(null);
    };

    const handleSave = (afterAction: AfterAction) => {
        let {
            alias,
            mrn,
            transplantDate,
            adpStatus,
            isActive,
            pairCoordinatorId,
            administrativeComments,
            comments,
            crossReferenceHint,
            inactiveReason,
            inactiveReasonDate,
            compatiblePairKPDReasons,
            compatiblePairOtherReason,
            isCompatiblePair,
            voucherConsentsReviewed
        } = state.data;

        let data: IRecipientGeneralDataRequest = {
            alias,
            mrn,
            transplantDate,
            adpStatus,
            isActive,
            pairCoordinatorId,
            comments,
            crossReferenceHint,
            inactiveReason,
            inactiveReasonDate,
            compatiblePairKPDReasons,
            compatiblePairOtherReason,
            isCompatiblePair,
            voucherConsentsReviewed,
            userName: props.app.user.username
        };

        props.showLoading('Saving General data, one moment please...').then(() => {
            props.updateGeneral(context.centerId, context.recipientId, data)
                .then(response => {
                    if (handleDuplicateError(response)) {
                        return;
                    }

                    if (response.data.success) {
                        let data = response.data.results;

                        isInactiveReasonOutdated(data)

                        data['comments'] = '';

                        dispatch({
                            type: 'updateHash',
                            value: hash(response.data.results)
                        });

                        context.updateHasChanges(false);

                        if (afterAction == null) {
                            toast.success('General information updated successfully');
                        }

                        context.doAfterAction(afterAction);

                        if (newConsentFile != null) {
                            let fileUpload: IFileUpload = {
                                docType: FileDocumentType.VoucherForm,
                                file: [newConsentFile],
                                append: false
                            };
                            props.uploadFile(context.centerId, context.recipientId, FileDocumentType.VoucherForm, fileUpload)
                                .then(() => {
                                    setNewConsentFile(null);
                                    getData();
                                });
                        }
                    } else {
                        context.updateErrors(ToggleType.Errors, [response.data.message]);
                    }
                })
                .catch(error => {
                    handleDuplicateError(error);
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    context.registerSave(props.saveRef, handleSave);

    if (state.data == null || context.headerInfo == null)
        return null;

    return (
        <ProfileWrapper>
            <Content
                centerId={context.centerId}
                recipientId={context.recipientId}
                lookupData={props.lookupData}
                generalData={state.data}
                isAdminLogin={props.app.user.group.isSuper}
                isInactiveReasonOutdated={isInactiveReasonOutdated(state.data)}
                onChange={handleDataChange}
                newConsentFile={newConsentFile}
                onViewFile={handleViewFile}
                onChangeConsentFile={handleChangeConsentFile}
                onRemoveConsentFile={handleRemoveConsentFile}
            />

            <DuplicateRecordModal
                show={showDuplicateRecord}
                message={duplicateMessage}
                onClose={handleCloseDuplicateRecord}
            />
        </ProfileWrapper>
    );
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getGeneral: (centerId, recipientId) => {
            return dispatch(RecipientActionCreators.getGeneral(centerId, recipientId))
        },
        updateGeneral: (centerId, recipientId, data) => {
            return dispatch(RecipientActionCreators.updateGeneral(centerId, recipientId, data));
        },
        downloadFile: (docType, id) => {
            return dispatch(FileActionCreators.downloadFile(docType, id));
        },
        uploadFile: (centerId, recipientId, docType, file) => {
            return dispatch(RecipientActionCreators.postRecipientRecords(centerId, recipientId, docType, file));
        }
    }
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(General);


